import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MarkersAPI } from '../api/MarkerEndpoints';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';
import HostEdits from './host-edits/HostEdits';
import { eventImageSrc, instagramPostInfo } from '../utils/eventImage';

import './dashboard/components/Marker-UI.css';
import '../components/pending-events/Pending-UI.css';
import PageTitle from '../components/PageTitle';

// The review queue: every marker a host submitted and no admin has accepted
// yet. Each card shows the picture the app will show — an Instagram post is
// resolved through the backend's thumbnail endpoint, the same source the app
// and the landing page use — next to the facts a reviewer needs at a glance;
// everything else sits behind "Details". Edits of markers that are already live
// are listed above the queue, as a field-by-field diff.

const FETCH_PAGE_SIZE = 5000;

// ---- Value helpers ---------------------------------------------------------

const unwrap = (value) => {
  if (value && typeof value === 'object' && 'present' in value) {
    return value.present ? (value.value ?? null) : null;
  }
  return value ?? null;
};

const text = (value) => {
  const v = unwrap(value);
  if (v == null) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
};

const list = (value) => {
  const v = unwrap(value);
  if (Array.isArray(v)) return v.map(text).filter(Boolean);
  return text(v) ? [text(v)] : [];
};

const clock = (value) => text(value).slice(0, 5);

// Hosts submit the ticket link. Only a web address may become a clickable link: a
// javascript: URL would run in the admin's session, which holds the admin token.
const isWebUrl = (value) => /^https?:\/\//i.test(value);

const dateLabel = (iso) => {
  if (!iso) return '';
  const date = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const priceLabel = (value) => {
  const raw = text(value);
  if (!raw) return '';
  const number = Number(raw);
  if (Number.isNaN(number)) return raw;
  return number === 0 ? 'Gratis' : `€ ${number}`;
};

const isEventMarker = (marker) => text(marker?.markerType).toLowerCase() === 'event';

const sortMarkers = (markers) => [...markers].sort((a, b) => {
  const eventA = isEventMarker(a);
  const eventB = isEventMarker(b);
  if (eventA !== eventB) return eventA ? -1 : 1;
  const dateA = text(a.eventDate) || '9999';
  const dateB = text(b.eventDate) || '9999';
  if (dateA !== dateB) return dateA.localeCompare(dateB);
  return text(a.title).localeCompare(text(b.title), 'de');
});

// ---- Icons (inline, 14px, stroke) ------------------------------------------

const Icon = ({ path, size = 14, strokeWidth = 1.8 }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
    strokeLinecap="round" strokeLinejoin="round" width={size} height={size} aria-hidden="true">
    {path}
  </svg>
);

const icons = {
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  pin: <><path d="M12 22s7-7.1 7-12a7 7 0 1 0-14 0c0 4.9 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" /></>,
  music: <><path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" /></>,
  tag: <><path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" /><circle cx="7.5" cy="7.5" r="1.5" /></>,
  euro: <><path d="M18 6a7 7 0 1 0 0 12" /><path d="M4 10h10M4 14h10" /></>,
  at: <><circle cx="12" cy="12" r="4" /><path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-4 8" /></>,
  key: <><circle cx="8" cy="15" r="4" /><path d="M10.9 12.1 21 2M15 8l3 3M18 5l3 3" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  refresh: <><path d="M3 12a9 9 0 1 0 9-9" /><path d="M3 3v6h6" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
  chevron: <path d="m6 9 6 6 6-6" />,
  external: <><path d="M14 4h6v6" /><path d="M20 4 10 14" /><path d="M20 14v6H4V4h6" /></>,
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>,
  inbox: <><path d="M22 12h-6l-2 3h-4l-2-3H2" /><path d="M5.5 5 2 12v7h20v-7l-3.5-7z" /></>,
  ticket: <><path d="M3 9a2 2 0 0 0 2-2V5h14v2a2 2 0 0 0 0 4v0a2 2 0 0 0 0 4v2H5v-2a2 2 0 0 0-2-2z" /><path d="M13 5v14" /></>,
  shield: <><path d="M12 22s8-3 8-10V5l-8-3-8 3v7c0 7 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>,
};

// ---- Card ------------------------------------------------------------------

function PendingCard({ marker, busy, disabled, onAccept, onReject }) {
  const [expanded, setExpanded] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);

  const isEvent = isEventMarker(marker);
  const title = text(marker.title) || 'Ohne Titel';
  const imageRaw = text(marker.image);
  const imageSrc = eventImageSrc(imageRaw);
  const post = instagramPostInfo(imageRaw);
  const postUrl = post ? `https://www.instagram.com/${post.type}/${post.code}/` : '';

  const eventDate = text(marker.eventDate);
  const start = clock(marker.startTime);
  const end = clock(marker.endTime);
  const address = text(marker.address);
  const genres = list(marker.musicGenre);
  const specialType = text(marker.specialType);
  const price = priceLabel(marker.entryPrice);
  const minAge = text(marker.minAge);
  const dressCode = text(marker.dressCode);
  const instagramName = text(marker.instagramName);
  const hostCode = text(marker.hostCode);
  const descriptionDe = text(marker.descriptionGerman);
  const descriptionEn = text(marker.descriptionEnglish);
  const ticketUrl = text(marker.ticketUrl);
  const boxOfficePrice = priceLabel(marker.boxOfficePrice);
  const boxOfficeAvailability = text(marker.boxOfficeAvailability);
  const awareness = unwrap(marker.hasAwarenessTeam) === true || text(marker.hasAwarenessTeam) === 'true';
  const artists = (Array.isArray(unwrap(marker.artists)) ? unwrap(marker.artists) : [])
    .map((artist) => (artist && typeof artist === 'object'
      ? { name: text(artist.name), instagram: text(artist.instagram) }
      : { name: text(artist), instagram: '' }))
    .filter((artist) => artist.name);
  const weekdays = (Array.isArray(unwrap(marker.weekdays)) ? unwrap(marker.weekdays) : [])
    .map((day) => ({ day: text(day?.day), open: clock(day?.open), close: clock(day?.close) }))
    .filter((day) => day.day && day.open);
  const latitude = Number(text(marker.latitude));
  const longitude = Number(text(marker.longitude));
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude) && text(marker.latitude) !== '';
  const mapSrc = hasCoordinates
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.006},${latitude - 0.004},${longitude + 0.006},${latitude + 0.004}&layer=mapnik&marker=${latitude},${longitude}`
    : '';

  const details = [descriptionDe, descriptionEn, ticketUrl, boxOfficePrice, boxOfficeAvailability, awareness, artists.length, weekdays.length, hasCoordinates].some(Boolean);
  const extras = [price, minAge ? `${minAge}+` : '', dressCode].filter(Boolean);

  return (
    <article className={`pe-card ${isEvent ? 'pe-card--event' : 'pe-card--club'}${busy ? ' pe-card--busy' : ''}`} aria-busy={busy}>
      <div className="pe-media">
        {imageSrc && !imageFailed ? (
          <img
            className="pe-media-img"
            src={imageSrc}
            alt={`Bild von ${title}`}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="pe-media-empty">
            <Icon path={icons.image} size={28} strokeWidth={1.4} />
            <span>{imageRaw ? 'Bild konnte nicht geladen werden' : 'Kein Bild hinterlegt'}</span>
          </div>
        )}
        <div className="pe-media-top">
          <span className={`badge ${isEvent ? 'badge-event' : 'badge-club'}`}>{isEvent ? 'Event' : 'Club'}</span>
          {isEvent && eventDate && <span className="badge badge-date">{dateLabel(eventDate)}</span>}
        </div>
        {postUrl && (
          <a className="pe-media-link" href={postUrl} target="_blank" rel="noreferrer">
            <Icon path={icons.external} size={12} strokeWidth={2} /> Instagram-Post
          </a>
        )}
      </div>

      <div className="pe-body">
        <h3 className="pe-title" title={title}>{title}</h3>

        <ul className="pe-facts">
          {isEvent && (start || end) && (
            <li className="pe-fact"><Icon path={icons.clock} /><span>{start || '?'} – {end || '?'}</span></li>
          )}
          <li className={`pe-fact${address ? '' : ' pe-fact--missing'}`}>
            <Icon path={icons.pin} /><span>{address || 'Keine Adresse'}</span>
          </li>
          <li className={`pe-fact${genres.length ? '' : ' pe-fact--missing'}`}>
            <Icon path={icons.music} /><span>{genres.length ? genres.join(' · ') : 'Kein Genre'}</span>
          </li>
          {specialType && (
            <li className="pe-fact"><Icon path={icons.tag} /><span>{specialType}</span></li>
          )}
          {extras.length > 0 && (
            <li className="pe-fact"><Icon path={icons.euro} /><span>{extras.join(' · ')}</span></li>
          )}
          {(instagramName || hostCode) && (
            <li className="pe-fact">
              <Icon path={instagramName ? icons.at : icons.key} />
              <span>
                {instagramName && `@${instagramName}`}
                {instagramName && hostCode && ' · '}
                {hostCode && <span className="pe-mono">{hostCode}</span>}
              </span>
            </li>
          )}
        </ul>

        {details && (
          <button
            type="button"
            className={`pe-more${expanded ? ' pe-more--open' : ''}`}
            onClick={() => setExpanded((open) => !open)}
            aria-expanded={expanded}
          >
            {expanded ? 'Weniger' : 'Details'} <Icon path={icons.chevron} size={12} strokeWidth={2} />
          </button>
        )}

        {expanded && (
          <div className="pe-details">
            {descriptionDe && <p className="pe-desc"><span className="pe-desc-lang">DE</span>{descriptionDe}</p>}
            {descriptionEn && <p className="pe-desc"><span className="pe-desc-lang">EN</span>{descriptionEn}</p>}

            {artists.length > 0 && (
              <div className="pe-chips" aria-label="Line-up">
                {artists.map((artist) => (
                  <span key={`${artist.name}|${artist.instagram}`} className="pe-chip">
                    {artist.name}
                    {artist.instagram && <span className="pe-chip-sub">@{artist.instagram}</span>}
                  </span>
                ))}
              </div>
            )}

            {(ticketUrl || boxOfficePrice || boxOfficeAvailability || awareness) && (
              <ul className="pe-facts pe-facts--details">
                {ticketUrl && (
                  <li className="pe-fact">
                    <Icon path={icons.ticket} />
                    {isWebUrl(ticketUrl)
                      ? <a className="pe-link" href={ticketUrl} target="_blank" rel="noreferrer">{ticketUrl}</a>
                      : <span className="pe-link">{ticketUrl}</span>}
                  </li>
                )}
                {(boxOfficePrice || boxOfficeAvailability) && (
                  <li className="pe-fact">
                    <Icon path={icons.euro} />
                    <span>Abendkassa {[boxOfficePrice, boxOfficeAvailability].filter(Boolean).join(' · ')}</span>
                  </li>
                )}
                {awareness && (
                  <li className="pe-fact"><Icon path={icons.shield} /><span>Awareness-Team vor Ort</span></li>
                )}
              </ul>
            )}

            {weekdays.length > 0 && (
              <div className="pe-chips" aria-label="Öffnungszeiten">
                {weekdays.map((day) => (
                  <span key={day.day} className="pe-chip">
                    <strong>{day.day.slice(0, 2)}</strong> {day.open}–{day.close}
                  </span>
                ))}
              </div>
            )}

            {hasCoordinates && (
              <div className="pe-map">
                <iframe title={`Karte ${title}`} src={mapSrc} loading="lazy" />
                <span className="pe-map-coords pe-mono">{latitude.toFixed(5)}, {longitude.toFixed(5)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <footer className="pe-actions">
        <button type="button" className="btn btn-primary pe-btn" onClick={onAccept} disabled={disabled}>
          <Icon path={icons.check} size={13} strokeWidth={2.4} /> {busy ? 'Speichere…' : 'Annehmen'}
        </button>
        <button type="button" className="btn btn-danger pe-btn" onClick={onReject} disabled={disabled}>
          <Icon path={icons.x} size={13} strokeWidth={2.4} /> Ablehnen
        </button>
        <span className="pe-id pe-mono">#{marker.id}</span>
      </footer>
    </article>
  );
}

// ---- Page ------------------------------------------------------------------

export default function PendingEvents() {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [rejecting, setRejecting] = useState(null); // marker | 'all'
  const [confirmAcceptAll, setConfirmAcceptAll] = useState(false);
  const [editsKey, setEditsKey] = useState(0);

  const fetchMarkers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await MarkersAPI.getAllMarkers({ page: 0, size: FETCH_PAGE_SIZE, _t: Date.now() });
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setMarkers(sortMarkers(items.filter((marker) => marker?.adminAccepted === false)));
    } catch (err) {
      console.error('Fehler beim Laden der Pending Events:', err);
      setError('Pending Events konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  const counts = useMemo(() => ({
    all: markers.length,
    event: markers.filter(isEventMarker).length,
    club: markers.filter((marker) => !isEventMarker(marker)).length,
  }), [markers]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return markers.filter((marker) => {
      if (filter === 'event' && !isEventMarker(marker)) return false;
      if (filter === 'club' && isEventMarker(marker)) return false;
      if (!needle) return true;
      const haystack = [marker.title, marker.address, marker.instagramName, marker.hostCode, ...list(marker.musicGenre)]
        .map(text).join(' ').toLowerCase();
      return haystack.includes(needle);
    });
  }, [markers, filter, query]);

  const accept = async (marker) => {
    if (!marker?.id) return;
    try {
      setBusyId(marker.id);
      setError(null);
      await MarkersAPI.approveMarker(marker.id);
      setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
    } catch (err) {
      console.error('Fehler beim Annehmen:', err);
      const message = err?.response?.data?.message || err?.message || 'Unbekannter Fehler';
      setError(`„${text(marker.title)}" konnte nicht angenommen werden (${err?.response?.status ?? '?'}): ${message}`);
    } finally {
      setBusyId(null);
    }
  };

  const confirmReject = async () => {
    const target = rejecting;
    setRejecting(null);
    if (!target) return;
    const targets = target === 'all' ? markers : [target];
    try {
      if (target === 'all') setBulkBusy(true); else setBusyId(target.id);
      setError(null);
      for (const marker of targets) {
        if (!marker?.id) continue;
        await MarkersAPI.deleteMarker(marker.id);
        setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
      }
    } catch (err) {
      console.error('Fehler beim Ablehnen:', err);
      setError('Ablehnen fehlgeschlagen. Die Liste wurde neu geladen.');
      await fetchMarkers();
    } finally {
      setBulkBusy(false);
      setBusyId(null);
    }
  };

  const acceptAll = async () => {
    setConfirmAcceptAll(false);
    if (markers.length === 0) return;
    try {
      setBulkBusy(true);
      setError(null);
      for (const marker of markers) {
        if (!marker?.id) continue;
        await MarkersAPI.approveMarker(marker.id);
        setMarkers((prev) => prev.filter((m) => m.id !== marker.id));
      }
    } catch (err) {
      console.error('Fehler beim Alle-Annehmen:', err);
      setError('Nicht alle Marker konnten angenommen werden. Die Liste wurde neu geladen.');
      await fetchMarkers();
    } finally {
      setBulkBusy(false);
    }
  };

  const disabled = loading || bulkBusy;

  const reload = () => {
    fetchMarkers();
    setEditsKey((key) => key + 1);
  };

  return (
    <div className="page pe-page">
      <div className="page-header">
        <PageTitle title="Freigaben" description="Neu eingetragene Events und Änderungen von Hosts. Prüfe sie und gib sie frei – erst dann erscheinen sie in der App." />
        <div className="pe-header-actions">
          <button type="button" className="btn btn-ghost pe-btn" onClick={reload} disabled={disabled}>
            <Icon path={icons.refresh} size={13} strokeWidth={2} /> Neu laden
          </button>
          <button type="button" className="btn btn-primary pe-btn" onClick={() => setConfirmAcceptAll(true)}
            disabled={disabled || markers.length === 0}>
            <Icon path={icons.check} size={13} strokeWidth={2.4} /> Alle annehmen
          </button>
          <button type="button" className="btn btn-danger pe-btn" onClick={() => setRejecting('all')}
            disabled={disabled || markers.length === 0}>
            <Icon path={icons.x} size={13} strokeWidth={2.4} /> Alle ablehnen
          </button>
        </div>
      </div>

      {confirmAcceptAll && (
        <div className="pe-confirm" role="alertdialog" aria-label="Alle annehmen bestätigen">
          <span>Alle <strong>{markers.length}</strong> offenen Marker annehmen? Sie erscheinen sofort in der App.</span>
          <div className="pe-confirm-actions">
            <button type="button" className="btn btn-ghost pe-btn" onClick={() => setConfirmAcceptAll(false)}>Abbrechen</button>
            <button type="button" className="btn btn-primary pe-btn" onClick={acceptAll}>Ja, alle annehmen</button>
          </div>
        </div>
      )}

      <HostEdits refreshKey={editsKey} />

      <div className="pe-toolbar">
        <div className="pe-chips pe-filters" role="tablist" aria-label="Filter">
          {[['all', 'Alle'], ['event', 'Events'], ['club', 'Clubs']].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={filter === key}
              className={`pe-filter${filter === key ? ' pe-filter--active' : ''}`}
              onClick={() => setFilter(key)}
            >
              {label} <span className="pe-filter-count">{counts[key]}</span>
            </button>
          ))}
        </div>
        <label className="pe-search">
          <Icon path={icons.search} size={14} />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Titel, Adresse, Instagram, Genre…"
            aria-label="Pending Events durchsuchen"
          />
        </label>
      </div>

      {error && (
        <div className="error-state" role="alert">
          <Icon path={icons.x} size={14} strokeWidth={2.2} /> {error}
        </div>
      )}

      {loading && markers.length === 0 && (
        <div className="pe-grid" aria-label="Lädt Pending Events">
          {[0, 1, 2].map((n) => <div key={n} className="pe-skeleton" />)}
        </div>
      )}

      {!loading && markers.length === 0 && !error && (
        <div className="pe-empty">
          <Icon path={icons.inbox} size={34} strokeWidth={1.3} />
          <strong>Nichts zu prüfen</strong>
          <span>Alle eingereichten Marker sind bearbeitet.</span>
        </div>
      )}

      {markers.length > 0 && visible.length === 0 && (
        <div className="pe-empty">
          <Icon path={icons.search} size={30} strokeWidth={1.3} />
          <strong>Keine Treffer</strong>
          <span>Kein offener Marker passt zu Filter und Suche.</span>
        </div>
      )}

      {visible.length > 0 && (
        <div className="pe-grid">
          {visible.map((marker) => (
            <PendingCard
              key={marker.id}
              marker={marker}
              busy={busyId === marker.id}
              disabled={disabled || busyId !== null}
              onAccept={() => accept(marker)}
              onReject={() => setRejecting(marker)}
            />
          ))}
        </div>
      )}

      <ConfirmDeleteModal
        open={rejecting !== null}
        title={rejecting === 'all' ? `Alle ${markers.length} offenen Marker ablehnen?` : undefined}
        message={rejecting === 'all'
          ? 'Jeder offene Marker wird dauerhaft gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.'
          : undefined}
        confirmLabel={rejecting === 'all' ? 'Alle ablehnen' : 'Ablehnen und löschen'}
        markerTitle={rejecting && rejecting !== 'all' ? text(rejecting.title) : undefined}
        onConfirm={confirmReject}
        onCancel={() => setRejecting(null)}
      />
    </div>
  );
}
